import { useMemo } from 'react';
import { consolidatedBusinessApi } from '../../services/consolidatedBusinessApi';
import { CommentsThread, type CommentsAdapter } from '../ui/CommentsThread';
import type { ConsBusinessCommentArm } from '../../types/consolidatedBusiness';

interface Props {
    arm: ConsBusinessCommentArm;
    /** groupKey for the 'Group' arm, CustomerID for the 'Customer' arm. */
    entityKey: string;
    canEdit: boolean;
    onChanged?: () => void;
}

/**
 * Consolidated-business binding of the shared CommentsThread. Used by the CB detail drawer
 * and by the Customer-360 Lost-Business tab — the same table and the same rows, so a comment
 * written on one page is visible on the other.
 *
 * The caller must pass key={entityKey} (or remount otherwise): the thread keeps its loaded
 * comments in state, so without it a row change would show the previous group's thread.
 */
export function ConsBusinessCommentsThread({ arm, entityKey, canEdit, onChanged }: Props) {
    // Memoised — CommentsThread's load effect depends on the adapter identity.
    const adapter = useMemo<CommentsAdapter>(() => ({
        // Group comments mirror into HubSpot as deal notes (migration 135); map the
        // domain sync fields onto the thread's generic ones so the chip renders.
        list: async () => (await consolidatedBusinessApi.listComments(arm, entityKey)).comments.map(c => ({
            ...c,
            SyncStatus: c.HubspotSyncStatus ?? null,
            SyncUrl: c.HubspotNoteUrl ?? null,
            SyncHint: c.HubspotSyncError ?? null,
        })),
        create: text => consolidatedBusinessApi.createComment(arm, entityKey, text),
        update: (id, text) => consolidatedBusinessApi.updateComment(id, text),
        remove: async id => {
            const result = await consolidatedBusinessApi.deleteComment(id);
            return result.HubspotSyncStatus === 'DeleteFailed'
                ? { warning: 'Comment deleted here, but the HubSpot note could not be removed. It stays on the deal until a retry succeeds.' }
                : result;
        },
        ...(arm === 'Group'
            ? { retrySync: (id: number) => consolidatedBusinessApi.retryCommentSync(id) }
            : {}),
    }), [arm, entityKey]);

    return <CommentsThread adapter={adapter} canEdit={canEdit} onChanged={onChanged} />;
}
